/**
 * Fact Strike — main container page.
 *
 * Screen state machine: BRIEFING → FLIGHT → DEBRIEF → (BRIEFING | ARCADE)
 * Uses useGameSession for API lifecycle.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameSession } from '@/hooks/useGameSession'
import { buildContact } from '@/components/fact-strike/distractors'
import BriefingRoom from '@/components/fact-strike/BriefingRoom'
import EngagementHUD from '@/components/fact-strike/EngagementHUD'
import Debrief from '@/components/fact-strike/Debrief'
import type {
  ContentSet,
  Contact,
  SortieResult,
  FactStrikeAnswerEvent,
  FactStrikeCompleteEvent,
  MasteryUpdate,
} from '@/components/fact-strike/types'

type Screen = 'briefing' | 'flight' | 'debrief'

const CONTACT_COUNT = 15

export default function FactStrike() {
  const navigate = useNavigate()
  const {
    session,
    contentSets,
    stats,
    isLoading,
    startSession,
    fetchContentSets,
    fetchItems,
    recordAttempt,
    endSession,
    abandonSession,
  } = useGameSession('fact-strike')

  const [screen, setScreen] = useState<Screen>('briefing')
  const [selectedSet, setSelectedSet] = useState<ContentSet | null>(null)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [sortieResult, setSortieResult] = useState<SortieResult | null>(null)
  const [initialized, setInitialized] = useState(false)
  // Every failure below used to be a bare console.error, so a kid tapping
  // "STEP TO AIRCRAFT" with no session (or an empty content set) just saw
  // nothing happen. Surface it on the briefing screen instead.
  const [launchError, setLaunchError] = useState<string | null>(null)
  const masteryUpdatesRef = useRef<MasteryUpdate[]>([])

  // Initialize: start session + fetch content sets
  useEffect(() => {
    if (initialized) return
    setInitialized(true)

    const init = async () => {
      try {
        setLaunchError(null)
        await startSession('solo')
        const sets = await fetchContentSets('math-facts')
        // Auto-select whatever the student is currently being tested on
        // (StudentContentSet.extra_data.math_focus, set via
        // scripts/set_math_focus.py), then multiplication, then anything.
        const focusedSet = sets.find((s) => s.focus?.focus_through)
        const multSet = sets.find((s) =>
          s.name.toLowerCase().includes('mult')
        )
        const defaultSet = focusedSet ?? multSet ?? sets[0]
        if (defaultSet) {
          setSelectedSet(defaultSet)
        } else {
          setLaunchError('No math fact sets are assigned to you yet.')
        }
      } catch (err) {
        console.error('Failed to initialize Fact Strike:', err)
        setLaunchError('Could not reach the ops center. Check your connection and reload.')
      }
    }
    init()
  }, [initialized, startSession, fetchContentSets])

  // Abandon session on unmount
  useEffect(() => {
    return () => {
      abandonSession()
    }
  }, [abandonSession])

  // Launch sortie: fetch items → build contacts → enter flight
  const handleLaunch = useCallback(async () => {
    if (!selectedSet) {
      setLaunchError('Pick an operation first.')
      return
    }
    if (!session) {
      setLaunchError('Still connecting to the ops center — try again in a moment.')
      return
    }

    setLaunchError(null)
    try {
      const items = await fetchItems(selectedSet.id, CONTACT_COUNT)
      if (items.length === 0) {
        setLaunchError(
          `No facts available in ${selectedSet.name}. Ask a grown-up to check the practice list.`,
        )
        return
      }

      const built = items.map((item) => buildContact(item))
      setContacts(built)
      masteryUpdatesRef.current = []
      setScreen('flight')
    } catch (err) {
      console.error('Failed to launch sortie:', err)
      setLaunchError('Could not load facts. Check your connection and try again.')
    }
  }, [selectedSet, session, fetchItems])

  // Handle each answer from Phaser
  const handleAnswer = useCallback(
    async (event: FactStrikeAnswerEvent) => {
      try {
        const update = await recordAttempt(
          event.itemId,
          event.studentAnswer,
          event.isCorrect,
          event.responseTimeMs,
        )
        masteryUpdatesRef.current.push(update)
      } catch (err) {
        console.error('Failed to record attempt:', err)
      }
    },
    [recordAttempt],
  )

  // Handle sortie completion
  const handleComplete = useCallback(
    async (event: FactStrikeCompleteEvent) => {
      try {
        const endResult = await endSession(event.totalScore, {
          bestStreak: event.bestStreak,
          accuracy: event.accuracy,
          avgTimeMs: event.avgTimeMs,
          contactCount: event.results.length,
          correctCount: event.results.filter((r) => r.isCorrect).length,
        })

        // Build weak contacts (incorrect or slow)
        const weakContacts = event.results.filter(
          (r) => !r.isCorrect || r.qualityRating <= 2 || r.attempts > 1,
        )

        const result: SortieResult = {
          contacts: event.results,
          totalContacts: event.results.length,
          correctCount: event.results.filter((r) => r.isCorrect).length,
          accuracy: event.accuracy,
          avgResponseTimeMs: event.avgTimeMs,
          bestStreak: event.bestStreak,
          totalScore: event.totalScore,
          weakContacts,
          xpEarned: endResult.xpEarned,
          masteryUpdates: masteryUpdatesRef.current,
        }

        setSortieResult(result)
        setScreen('debrief')
      } catch (err) {
        console.error('Failed to end session:', err)
        // Still show debrief with what we have
        const weakContacts = event.results.filter(
          (r) => !r.isCorrect || r.qualityRating <= 2 || r.attempts > 1,
        )
        setSortieResult({
          contacts: event.results,
          totalContacts: event.results.length,
          correctCount: event.results.filter((r) => r.isCorrect).length,
          accuracy: event.accuracy,
          avgResponseTimeMs: event.avgTimeMs,
          bestStreak: event.bestStreak,
          totalScore: event.totalScore,
          weakContacts,
          xpEarned: 0,
        })
        setScreen('debrief')
      }
    },
    [endSession],
  )

  // Fly Again: start new session, return to briefing
  const handleFlyAgain = useCallback(async () => {
    try {
      await startSession('solo')
      setScreen('briefing')
    } catch (err) {
      console.error('Failed to restart:', err)
      setScreen('briefing')
    }
  }, [startSession])

  const handleBackToArcade = useCallback(() => {
    navigate('/student/arcade')
  }, [navigate])

  // Render current screen
  switch (screen) {
    case 'briefing':
      return (
        <BriefingRoom
          session={session}
          contentSets={contentSets}
          selectedSet={selectedSet}
          stats={stats}
          isLoading={isLoading}
          error={launchError}
          onSelectSet={setSelectedSet}
          onLaunch={handleLaunch}
        />
      )

    case 'flight':
      return (
        <EngagementHUD
          contacts={contacts}
          onAnswer={handleAnswer}
          onComplete={handleComplete}
        />
      )

    case 'debrief':
      return sortieResult ? (
        <Debrief
          result={sortieResult}
          onFlyAgain={handleFlyAgain}
          onBackToArcade={handleBackToArcade}
        />
      ) : null
  }
}
